import { IconButton, TextButton } from '@creditornot/cb-button';
import { MagnifyingGlass, MoreHorizontal, Plus } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { MenuItemGroup, MenuLinkItem } from '@creditornot/cb-menu';
import { Empty } from '@creditornot/cb-placeholders';
import { LegacyPopover } from '@creditornot/cb-popover';
import { Spinner } from '@creditornot/cb-progress';
import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { StringParam, useQueryParam } from 'use-query-params';

import { searchItemsInPickingAreas } from 'apis/picker-api/api';
import { assignItemsToPickingArea, unassignItemsFromArea } from 'apis/picker-api/api';
import { APIErrorNotification } from 'common/components';
import PageHeader from 'common/components/PageHeader';
import { useDebounce } from 'common/hooks';

import AreaRow from './AreaRow';
import CreateAreaModal from './CreateAreaModal';
import CreateFromCategoriesModal from './CreateFromCategoriesModal';
import CustomAreasList from './CustomAreasList';
import DeleteAreasModal from './DeleteAreasModal';
import EditAreaModal from './EditAreaModal';
import GetStarted from './GetStarted';
import ItemList from './ItemList';
import MoveItemsModal from './MoveItemsModal';
import PickingArea from './PickingArea';
import PickingAreasDnDContext from './PickingAreasDnDContext';
import UnassignedArea from './UnassignedArea';
import { UNASSIGNED_AREA_ID } from '../constants';
import useModalManagers from '../hooks/useModalManagers';
import usePickingAreas from '../hooks/usePickingAreas';
import { isHeaderType, transformSearchResultsToListElements } from '../utils';

type Props = {
  className?: string;
  venueId: string;
  menuId: string | null;
};

const Root = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  color: ${cbSemanticCssVars.textPrimary};
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const TopActionsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const SearchInput = styled(Input)`
  margin-inline-end: 16px;
`;

const StyledPopover = styled(LegacyPopover)`
  margin-inline-start: 16px;
`;

const PopoverContent = styled(MenuItemGroup)`
  padding: 0 8px;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
`;

const SearchResultsContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const EmptyContainer = styled.div`
  width: 100%;
  margin-block-start: 32px;
  text-align: center;
`;

const ErrorContainer = styled.div`
  width: 480px;
  margin: 32px auto;
`;

const LeftArea = styled.div<{ fullWidth?: boolean }>`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : '480px')};
`;

const StyledCustomAreasList = styled(CustomAreasList)`
  flex: 1;
`;

const UnassignedItemsRow = styled(AreaRow)`
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  border-block-end: none;
`;

const RightArea = styled.div`
  min-width: 0;
  border-inline-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  flex: 1;
`;

const StyledEmpty = styled(Empty)`
  display: block;
  margin: 32px auto;
`;

const PickingRoutePage = ({ className, venueId, menuId }: Props) => {
  const intl = useIntl();

  const [searchInput, setSearchInput] = useQueryParam('search', StringParam);

  const {
    menuItems,
    activeAreaId,
    setActiveAreaId,
    activeAreaItems,
    setActiveAreaItems,
    pickingAreas,
    setPickingAreas,
    state,
    error,
    refetchPickingAreas,
    refetchActiveArea,
  } = usePickingAreas(venueId, menuId);

  const customAreas = useMemo(() => pickingAreas?.filter(area => area.id !== UNASSIGNED_AREA_ID), [pickingAreas]);
  const unassignedArea = useMemo(() => pickingAreas?.find(area => area.id === UNASSIGNED_AREA_ID), [pickingAreas]);

  const activeAreaName = useMemo(
    () => pickingAreas?.find(area => area.id === activeAreaId)?.name || '<area name not found>',
    [activeAreaId, pickingAreas],
  );

  const searchItems = useMutation(
    async (query: string | null | undefined) => {
      if (!query?.length || !menuItems) {
        return undefined;
      }
      const searchResults = await searchItemsInPickingAreas(venueId, { query });
      return transformSearchResultsToListElements(searchResults, menuItems);
    },
    {
      onError: () => {
        toast.error(intl.formatMessage({ id: 'picking-route.toast-generic-error' }));
      },
    },
  );

  const debouncedHandleSearch = useDebounce(searchItems.mutate, 300);

  useEffect(() => {
    debouncedHandleSearch(searchInput);
  }, [debouncedHandleSearch, searchInput]);

  const isInSearchMode = useMemo(
    () => !!searchInput && !!searchItems.data?.length,
    [searchInput, searchItems.data?.length],
  );

  const assignItems = useMutation(
    async ({ areaId, itemIds }: { areaId: string; itemIds: string[] }) => {
      if (!activeAreaId) {
        throw new Error('Active picking area not defined, this should not happen!');
      }
      if (areaId !== UNASSIGNED_AREA_ID) {
        await assignItemsToPickingArea(venueId, areaId, itemIds);
      } else {
        await unassignItemsFromArea(venueId, activeAreaId, itemIds);
      }
      await refetchPickingAreas();
    },
    {
      onSuccess: () => {
        if (isInSearchMode) {
          searchItems.mutate(searchInput);
        }
      },
      onError: () => {
        toast.error(intl.formatMessage({ id: 'picking-route.toast-generic-error' }));
      },
    },
  );

  const {
    moveItemsModalProps,
    createFromCatsModalProps,
    createAreaModalProps,
    editAreaModalProps,
    deleteAreaModalProps,
    dispatchModalAction,
  } = useModalManagers({
    venueId,
    activeAreaId,
    pickingAreas,
    unassignedAreaHeaders: activeAreaId === UNASSIGNED_AREA_ID ? activeAreaItems.filter(isHeaderType) : undefined,
    isInSearchMode,
    refetchPickingAreas,
    refetchActiveArea,
    setActiveAreaId,
    onMoveItemsSuccess: () => (isInSearchMode ? searchItems.mutate(searchInput) : undefined),
  });

  return (
    <Root className={className}>
      <PageHeader
        title={intl.formatMessage({ id: 'picking-route.page-title' })}
        subtitle={intl.formatMessage({ id: 'picking-route.page-subtitle' })}
      >
        <TopActionsContainer>
          <SearchInput
            onClearClick={() => setSearchInput('')}
            value={searchInput || ''}
            onChange={e => setSearchInput(e.currentTarget.value)}
            placeholder={intl.formatMessage({ id: 'picking-route.search-all-items' })}
            icon={<MagnifyingGlass />}
          />
          <TextButton
            variant="blue"
            icon={<Plus />}
            disabled={state !== 'settled'}
            onClick={() => dispatchModalAction({ type: 'create-area-start', itemsToAssign: [] })}
          >
            <FormattedMessage id="picking-route.create-new" />
          </TextButton>
          <StyledPopover
            showOnClick
            showOnHover={false}
            renderArrow={false}
            portalElement={null}
            contentZIndex={1}
            placement="bottom-start"
            closeOnClickInside
            disabled={state !== 'settled' || customAreas?.length === 0}
            content={
              <PopoverContent>
                <MenuLinkItem
                  variant="red"
                  onClick={() =>
                    dispatchModalAction({
                      type: 'delete-area-start',
                      areaIdsToDelete: customAreas?.map(area => area.id) || [],
                    })
                  }
                >
                  <FormattedMessage id="picking-route.delete-all-areas" />
                </MenuLinkItem>
              </PopoverContent>
            }
          >
            <IconButton variant="blue" size="small" disabled={state !== 'settled' || customAreas?.length === 0}>
              <MoreHorizontal />
            </IconButton>
          </StyledPopover>
        </TopActionsContainer>
      </PageHeader>
      <Content>
        {searchItems.isLoading && (
          <EmptyContainer>
            <Spinner size="large" />
          </EmptyContainer>
        )}
        {searchItems.data && (
          <SearchResultsContainer>
            {searchItems.data.length > 0 ? (
              <ItemList
                items={searchItems.data}
                menuId={menuId}
                onMoveItems={itemsData => dispatchModalAction({ type: 'move-items-start', itemsToAssign: itemsData })}
                onRemoveItem={itemId => assignItems.mutate({ areaId: UNASSIGNED_AREA_ID, itemIds: [itemId] })}
              />
            ) : (
              <EmptyContainer>
                <Empty title={intl.formatMessage({ id: 'picking-route.search-no-match' })} />
              </EmptyContainer>
            )}
          </SearchResultsContainer>
        )}
        {!searchItems.data && !searchItems.isLoading && (
          <>
            {state === 'no-menu' && <StyledEmpty title={intl.formatMessage({ id: 'menu.noMenuForVenue' })} />}
            {state === 'loading' && (
              <EmptyContainer>
                <Spinner size="large" />
              </EmptyContainer>
            )}
            {state === 'error' && (
              <ErrorContainer>
                <APIErrorNotification error={error} />
              </ErrorContainer>
            )}
            {(state === 'settled' || state === 'loading-single-area') && (
              <PickingAreasDnDContext
                venueId={venueId}
                pickingAreas={pickingAreas}
                activeAreaId={activeAreaId}
                activeAreaItems={activeAreaItems}
                onSetPickingAreas={setPickingAreas}
                onSetActiveAreaItems={setActiveAreaItems}
                onAssignItemsToArea={assignItems.mutateAsync}
                onDropItemsInDropzone={itemIds =>
                  dispatchModalAction({ type: 'create-area-start', itemsToAssign: itemIds })
                }
              >
                {({ draggedAreaId, draggedItemIds }) => (
                  <>
                    <LeftArea fullWidth={!activeAreaId}>
                      {customAreas?.length === 0 && (
                        <GetStarted
                          onCreateArea={() => dispatchModalAction({ type: 'create-area-start', itemsToAssign: [] })}
                          onCreateFromCategories={() => dispatchModalAction({ type: 'create-from-cats-start' })}
                        />
                      )}
                      {customAreas && customAreas.length > 0 && (
                        <StyledCustomAreasList
                          areas={customAreas}
                          activeAreaId={activeAreaId}
                          draggedAreaId={draggedAreaId}
                          onSelectArea={setActiveAreaId}
                        />
                      )}
                      {unassignedArea && (
                        <UnassignedItemsRow
                          area={{
                            ...unassignedArea,
                            name: intl.formatMessage({ id: 'picking-route.unassigned-items' }),
                          }}
                          selected={activeAreaId === UNASSIGNED_AREA_ID}
                          onClick={() => setActiveAreaId(UNASSIGNED_AREA_ID)}
                        />
                      )}
                    </LeftArea>
                    {!!activeAreaId && (
                      <RightArea>
                        {activeAreaId === UNASSIGNED_AREA_ID ? (
                          <UnassignedArea
                            items={activeAreaItems}
                            isLoadingItems={state === 'loading-single-area'}
                            menuId={menuId}
                            draggedItemIds={draggedItemIds}
                            header={<FormattedMessage id="picking-route.unassigned-items" />}
                            onClose={() => setActiveAreaId(null)}
                            onMoveItems={itemsData =>
                              dispatchModalAction({ type: 'move-items-start', itemsToAssign: itemsData })
                            }
                          />
                        ) : (
                          <PickingArea
                            items={activeAreaItems}
                            isLoadingItems={state === 'loading-single-area'}
                            menuId={menuId}
                            draggedItemIds={draggedItemIds}
                            header={activeAreaName}
                            onEdit={() => dispatchModalAction({ type: 'edit-area-start' })}
                            onDelete={() =>
                              dispatchModalAction({ type: 'delete-area-start', areaIdsToDelete: [activeAreaId] })
                            }
                            onClose={() => setActiveAreaId(null)}
                            onMoveItems={itemsData =>
                              dispatchModalAction({ type: 'move-items-start', itemsToAssign: itemsData })
                            }
                            onRemoveItem={itemId =>
                              assignItems.mutate({ areaId: UNASSIGNED_AREA_ID, itemIds: [itemId] })
                            }
                          />
                        )}
                      </RightArea>
                    )}
                  </>
                )}
              </PickingAreasDnDContext>
            )}
          </>
        )}
      </Content>
      {moveItemsModalProps.isOpen && <MoveItemsModal {...moveItemsModalProps} />}
      {createFromCatsModalProps.isOpen && <CreateFromCategoriesModal {...createFromCatsModalProps} />}
      {createAreaModalProps.isOpen && <CreateAreaModal {...createAreaModalProps} />}
      {editAreaModalProps.isOpen && <EditAreaModal {...editAreaModalProps} />}
      {deleteAreaModalProps.isOpen && <DeleteAreasModal {...deleteAreaModalProps} />}
    </Root>
  );
};

export default PickingRoutePage;
